import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { useEdmDocumentationHistoryCountersQuery } from 'graphql-common';
import TabBar from '@lib/components/TabBar/TabBar';
import { TabBarItem as TabBarItemType } from '@lib/components/TabBar/constants';
import getDocumentationFilePermissions from '@lib/utils/documentation/getDocumentationFilePermissions';
import useEdmDocumentationQueryHook from 'utils/fetch/hooks/useEdmDocumentationQueryHook';
import HistoryActivityTab from './HistoryActivityTab';
import HistoryVersionsTab from './HistoryVersionsTab';
import HistoryViewsTab from './HistoryViewsTab';
import styles from './DocumentViewHistory.module.scss';

type Props = {
  documentQueryHookResult: ReturnType<typeof useEdmDocumentationQueryHook>;
  isFullAdmin?: boolean;
  isViewLocked?: boolean;
  activityRefetchKey?: number;
};

const HISTORY_TABS = {
  activity: 'activity',
  versions: 'versions',
  views: 'views',
};

export default function DocumentViewHistory({
  documentQueryHookResult,
  isFullAdmin,
  isViewLocked,
  activityRefetchKey,
}: Props) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(HISTORY_TABS.activity);
  const documentationId = documentQueryHookResult.data?.id;
  const filePermissions = getDocumentationFilePermissions(
    documentQueryHookResult.data,
  );
  // The Views sub-tab is a premium upgrade affordance when locked — hide it
  // for non-Full-Admin Users who can't act on the upgrade CTA.
  const isViewsTabAvailable = !(isViewLocked && !isFullAdmin);

  const { data: countersData, refetch: refetchCounters } =
    useEdmDocumentationHistoryCountersQuery({
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      skip: !documentationId,
      variables: {
        documentationId: documentationId || '',
        documentationIdFilter: documentationId || '',
        isViewLocked,
      },
    });

  const versionsCount = _get(
    countersData,
    'versions.metadata.totalCount',
    0,
  ) as number;
  const viewsCount = _get(
    countersData,
    'views.metadata.totalCount',
    0,
  ) as number;

  const tabs: TabBarItemType[] = useMemo(
    () => [
      { label: t('activity'), value: HISTORY_TABS.activity },
      {
        label: t('versions'),
        value: HISTORY_TABS.versions,
        counter: versionsCount,
      },
      ...(isViewsTabAvailable
        ? [
            {
              label: t('views'),
              value: HISTORY_TABS.views,
              counter: viewsCount,
            },
          ]
        : []),
    ],
    [t, versionsCount, viewsCount, isViewsTabAvailable],
  );

  const onActiveChange = useCallback(
    ({ value }: TabBarItemType) => {
      if (value === activeTab) return;
      setActiveTab(value);
      refetchCounters();
    },
    [activeTab, refetchCounters],
  );

  return (
    <div className={styles.container}>
      <div className={styles.tabsContainer}>
        <TabBar
          items={tabs}
          active={activeTab}
          onActiveChange={onActiveChange}
          className={styles.tabBar}
        />
      </div>
      <div className={styles.content}>
        {activeTab === HISTORY_TABS.activity && documentationId && (
          <HistoryActivityTab
            key={activityRefetchKey}
            documentationId={documentationId}
            isFullAdmin={isFullAdmin}
            isViewLocked={isViewLocked}
            filePermissions={filePermissions}
          />
        )}
        {activeTab === HISTORY_TABS.versions && documentationId && (
          <HistoryVersionsTab
            documentationId={documentationId}
            filePermissions={filePermissions}
          />
        )}
        {activeTab === HISTORY_TABS.views &&
          isViewsTabAvailable &&
          documentationId && (
            <HistoryViewsTab documentationId={documentationId} />
          )}
      </div>
    </div>
  );
}
